import mixpanel from 'mixpanel-browser'
import * as braze from '@braze/web-sdk'
import * as Sentry from '@sentry/react'

const BASE_ANALYTICS_NAMESPACE = 'webOnboarding'

const isProduction = import.meta.env.VITE_ENV === 'production'

type UserProfile = {
  $email?: string
  $name?: string
  $phone?: string
}

type SuperProperties = {
  org_id?: string
}

type BrazeAttr = {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  orgKey?: string
  orgId?: string
  orgParentId?: string
}

export type EventProperties = {
  [key: string]: string | number | boolean | null | number[]
}

export const fireMixpanelEvent = (
  eventName: string,
  properties?: EventProperties
) => {
  const evt = `${BASE_ANALYTICS_NAMESPACE}:${eventName}`
  if (isProduction) {
    mixpanel.track(evt, properties)
  } else {
    console.log('Tracking event: ', evt, properties)
  }
}

// need to link the current mixpanel user to the user id
export const identifyAnalyticsUser = (userId: string, profile: UserProfile) => {
  if (isProduction) {
    Sentry.setUser({ id: userId })
    mixpanel.alias(userId, mixpanel.get_distinct_id())
    mixpanel.identify(userId)
    mixpanel.people.set(profile)
  } else {
    console.log(`Registering alias ${userId}`)
  }
}

export const identifyBrazeUser = (
  userId: string,
  { firstName, lastName, email, phone, orgId, orgKey, orgParentId }: BrazeAttr
) => {
  if (isProduction) {
    braze.changeUser(userId)
    const user = braze.getUser()
    if (!user) return
    if (firstName) user.setFirstName(firstName)
    if (lastName) user.setLastName(lastName)
    if (email) user.setEmail(email)
    if (phone) user.setPhoneNumber(phone)
    if (orgKey) user.setCustomUserAttribute('org_key', orgKey)
    if (orgId) user.setCustomUserAttribute('org_id', orgId)
    if (orgParentId) user.setCustomUserAttribute('org_parent_id', orgParentId)
  } else {
    console.log(`Registering Braze attributes for user id: ${userId}`)
  }
}

// registers properties in mixpanel as we get them
const setMixpanelProperty = (
  prop: 'name' | 'email' | 'phone' | 'org_key' | 'org_id' | 'deeplink_url',
  value: string
) => {
  mixpanel.register({
    [`$${prop}`]: value,
  })
}

export const setAnalyticsEmail = (value: string) => {
  if (isProduction) {
    setMixpanelProperty('email', value)
    const user = braze.getUser()
    if (user) user.setEmail(value)
  } else {
    console.log(`Registering analytics email property with value "${value}"`)
  }
}

export const setAnalyticsPhone = (value: string) => {
  if (isProduction) {
    setMixpanelProperty('phone', value)
    const user = braze.getUser()
    if (user) user.setPhoneNumber(value)
  } else {
    console.log(`Registering analytics phone property with value "${value}"`)
  }
}

export const setAnalyticsFullName = (value: string) => {
  if (isProduction) {
    setMixpanelProperty('name', value)
  } else {
    console.log(
      `Registering analytics full name property with value "${value}"`
    )
  }
}

export const setAnalyticsOrgKey = (value: string) => {
  if (isProduction) {
    setMixpanelProperty('org_key', value)
    const user = braze.getUser()
    if (user) user.setCustomUserAttribute('org_key', value)
  } else {
    console.log(`Registering analytics orgkey property with value "${value}"`)
  }
}

export const setAnalyticsOrgId = (value: string | number) => {
  if (isProduction) {
    setMixpanelProperty('org_id', `${value}`)
    const user = braze.getUser()
    if (user) user.setCustomUserAttribute('org_id', value)
  } else {
    console.log(`Registering analytics orgid property with value "${value}"`)
  }
}

export const setAnalyticsDeeplinkUrl = (value: string) => {
  if (isProduction) {
    setMixpanelProperty('deeplink_url', value)
    const user = braze.getUser()
    if (user) user.setCustomUserAttribute('deeplink_url', value)
  } else {
    console.log(
      `Registering analytics deeplink url property with value "${value}"`
    )
  }
}

export const setSuperProperties = (properties: SuperProperties) => {
  if (isProduction) {
    mixpanel.register(properties)
  }
}
